import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import { useToast } from '@/hooks/use-toast'
import { InviteUserModal } from '../modals/InviteUserModal'
import { EditUserModal } from '../modals/EditUserModal'
import { useUsersListQuery, useManageUserMutation } from '@/features/users/hooks'
import type { UserListItem } from '@/features/users/types'
import { UsersHeader } from '../management/UsersHeader'
import { UsersTable } from '../management/UsersTable'
import { UsersEmptyState } from '../management/UsersEmptyState'

export function UsersManagementContainer() {
  const { user: authUser } = useAuth()
  const { success: toastSuccess, error: toastError } = useToast()
  const queryClient = useQueryClient()
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null)

  const { data: users, isLoading, error } = useUsersListQuery()

  const manageUserMutation = useManageUserMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toastSuccess('User updated', 'User updated successfully')
    },
    onError: (mutationError) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to update user'
      toastError('Failed to update user', message)
    }
  })

  const handleEditUser = (user: UserListItem) => {
    setSelectedUser(user)
    setEditModalOpen(true)
  }

  const handleToggleActiveStatus = (userId: string, isActive: boolean) => {
    manageUserMutation.mutate({
      action: isActive ? 'deactivate' : 'activate',
      userId
    })
  }

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      manageUserMutation.mutate({
        action: 'delete',
        userId
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">Error loading users</h3>
            <p className="text-gray-500">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const userCount = users?.length || 0

  return (
    <div className="space-y-6">
      <UsersHeader onInviteClick={() => setInviteModalOpen(true)} />

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            {userCount} total users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {userCount > 0 ? (
            <UsersTable
              users={users || []}
              currentUserId={authUser?.id}
              onEdit={handleEditUser}
              onToggleStatus={handleToggleActiveStatus}
              onDelete={handleDeleteUser}
            />
          ) : (
            <UsersEmptyState onInviteClick={() => setInviteModalOpen(true)} />
          )}
        </CardContent>
      </Card>

      <InviteUserModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['users'] })
          setInviteModalOpen(false)
        }}
      />

      {selectedUser && (
        <EditUserModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          user={selectedUser}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['users'] })
            setEditModalOpen(false)
            setSelectedUser(null)
          }}
        />
      )}
    </div>
  )
}
